import { useMemo, useEffect } from "react";
import {
  Box,
  Button,
  Container,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import { Add, Remove, DeleteOutline, ShoppingCartOutlined } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useApp } from "../context/AuthContext";

export default function StoreCheckout() {
  const navigate = useNavigate();

  const {
    cartItems,
    removeFromCart,
    updateCartItemQuantity,
    getCartSubtotal,
    getWhatsAppCheckoutLink,
  } = useApp();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const total = useMemo(() => getCartSubtotal(), [getCartSubtotal]);

  return (
    <Box
      component="section"
      sx={{
        py: { xs: 4, md: 6 },
        bgcolor: "#f7f7f7",
        minHeight: "100%",
      }}
    >
      <Container maxWidth="lg">
        <Stack spacing={3}>
          <Box>
            <Typography
              sx={{
                fontSize: { xs: 28, md: 38 },
                fontWeight: 900,
                color: "#111",
                letterSpacing: "-0.02em",
              }}
            >
              Detalle de compra
            </Typography>

            <Typography
              sx={{
                mt: 1,
                fontSize: { xs: 14, md: 15 },
                color: "rgba(17,17,17,0.7)",
              }}
            >
              Revisa tus productos y finaliza tu pedido por WhatsApp.
            </Typography>

            <Typography
              sx={{
                mt: 0.5,
                fontSize: 12.5,
                fontWeight: 600,
                color: "rgba(17,17,17,0.55)",
              }}
            >
              Todos los precios incluyen IVA
            </Typography>
          </Box>

          {cartItems.length === 0 ? (
            <Box
              sx={{
                bgcolor: "#fff",
                borderRadius: "24px",
                border: "1px solid rgba(0,0,0,0.08)",
                p: { xs: 3, md: 5 },
                textAlign: "center",
              }}
            >
              <ShoppingCartOutlined sx={{ fontSize: 54, color: "rgba(0,0,0,0.3)" }} />

              <Typography
                sx={{
                  mt: 2,
                  fontSize: 24,
                  fontWeight: 800,
                  color: "#111",
                }}
              >
                Tu carrito está vacío
              </Typography>

              <Typography
                sx={{
                  mt: 1,
                  fontSize: 14,
                  color: "rgba(17,17,17,0.65)",
                }}
              >
                Añade productos desde la tienda para continuar con tu pedido.
              </Typography>

              <Button
                onClick={() => navigate("/tienda")}
                sx={{
                  mt: 3,
                  minWidth: 200,
                  height: 46,
                  borderRadius: "14px",
                  textTransform: "none",
                  fontWeight: 800,
                  bgcolor: "#111",
                  color: "#fff",
                  "&:hover": {
                    bgcolor: "#000",
                  },
                }}
              >
                Ir a la tienda
              </Button>
            </Box>
          ) : (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", lg: "1.5fr 0.8fr" },
                gap: 3,
                alignItems: "start",
              }}
            >
              <Stack spacing={2}>
                {cartItems.map((item) => {
                  const unitPrice = Number(item.price || 0);
                  const subtotal = unitPrice * Number(item.quantity || 0);
                  const codeText = item.code || item.sku || "";

                  return (
                    <Box
                      key={item.key}
                      sx={{
                        bgcolor: "#fff",
                        borderRadius: "22px",
                        border: "1px solid rgba(0,0,0,0.08)",
                        p: { xs: 2, md: 2.5 },
                      }}
                    >
                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: { xs: "88px 1fr", md: "110px 1fr auto" },
                          gap: 2,
                          alignItems: "center",
                        }}
                      >
                        <Box
                          sx={{
                            width: { xs: 88, md: 110 },
                            height: { xs: 88, md: 110 },
                            borderRadius: "18px",
                            bgcolor: "#f4f4f4",
                            overflow: "hidden",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <Box
                            component="img"
                            src={item.image}
                            alt={item.name}
                            sx={{
                              width: "100%",
                              height: "100%",
                              objectFit: "contain",
                            }}
                          />
                        </Box>

                        <Box sx={{ minWidth: 0 }}>
                          <Typography
                            sx={{
                              fontSize: { xs: 15, md: 17 },
                              fontWeight: 800,
                              color: "#111",
                              lineHeight: 1.2,
                            }}
                          >
                            {item.name}
                          </Typography>

                          {!!item.variantLabel && (
                            <Typography
                              sx={{
                                mt: 0.7,
                                fontSize: 13,
                                color: "rgba(17,17,17,0.72)",
                              }}
                            >
                              {item.variantLabel}
                            </Typography>
                          )}

                          {!!codeText && (
                            <Typography
                              sx={{
                                mt: 0.7,
                                fontSize: 12,
                                color: "rgba(17,17,17,0.54)",
                              }}
                            >
                              {item.code ? `Código: ${item.code}` : `SKU: ${item.sku}`}
                            </Typography>
                          )}

                          <Box
                            sx={{
                              mt: 1.2,
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              flexWrap: "wrap",
                            }}
                          >
                            {item.originalPrice && item.originalPrice > unitPrice ? (
                              <>
                                <Typography
                                  sx={{
                                    fontSize: 13,
                                    color: "rgba(17,17,17,0.45)",
                                    textDecoration: "line-through",
                                  }}
                                >
                                  ${Number(item.originalPrice).toFixed(2)}
                                </Typography>
                                <Typography
                                  sx={{
                                    fontSize: 18,
                                    fontWeight: 900,
                                    color: "#d32f2f",
                                  }}
                                >
                                  ${unitPrice.toFixed(2)}
                                </Typography>
                              </>
                            ) : (
                              <Typography
                                sx={{
                                  fontSize: 18,
                                  fontWeight: 900,
                                  color: "#111",
                                }}
                              >
                                ${unitPrice.toFixed(2)}
                              </Typography>
                            )}
                          </Box>
                        </Box>

                        <Stack
                          direction={{ xs: "row", md: "column" }}
                          spacing={1.2}
                          alignItems={{ xs: "center", md: "flex-end" }}
                          justifyContent="space-between"
                          sx={{
                            gridColumn: { xs: "1 / -1", md: "auto" },
                            mt: { xs: 1, md: 0 },
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              border: "1px solid rgba(0,0,0,0.12)",
                              borderRadius: "14px",
                              overflow: "hidden",
                              height: 42,
                            }}
                          >
                            <IconButton
                              onClick={() =>
                                updateCartItemQuantity(item.key, Number(item.quantity || 0) - 1)
                              }
                              sx={{
                                width: 42,
                                height: 42,
                                borderRadius: 0,
                              }}
                            >
                              <Remove />
                            </IconButton>

                            <Typography
                              sx={{
                                minWidth: 40,
                                textAlign: "center",
                                fontSize: 15,
                                fontWeight: 800,
                                color: "#111",
                              }}
                            >
                              {item.quantity}
                            </Typography>

                            <IconButton
                              onClick={() =>
                                updateCartItemQuantity(item.key, Number(item.quantity || 0) + 1)
                              }
                              sx={{
                                width: 42,
                                height: 42,
                                borderRadius: 0,
                              }}
                            >
                              <Add />
                            </IconButton>
                          </Box>

                          <Typography
                            sx={{
                              fontSize: 16,
                              fontWeight: 800,
                              color: "#111",
                            }}
                          >
                            ${subtotal.toFixed(2)}
                          </Typography>

                          <IconButton
                            onClick={() => removeFromCart(item.key)}
                            sx={{
                              width: 42,
                              height: 42,
                              borderRadius: "12px",
                              border: "1px solid rgba(211,47,47,0.18)",
                              color: "#d32f2f",
                            }}
                          >
                            <DeleteOutline />
                          </IconButton>
                        </Stack>
                      </Box>
                    </Box>
                  );
                })}
              </Stack>

              <Box
                sx={{
                  bgcolor: "#fff",
                  borderRadius: "24px",
                  border: "1px solid rgba(0,0,0,0.08)",
                  p: { xs: 2.5, md: 3 },
                  position: { lg: "sticky" },
                  top: { lg: 120 },
                }}
              >
                <Typography
                  sx={{
                    fontSize: 22,
                    fontWeight: 800,
                    color: "#111",
                  }}
                >
                  Resumen
                </Typography>

                <Box
                  sx={{
                    mt: 2,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 15,
                      color: "rgba(17,17,17,0.65)",
                    }}
                  >
                    Total
                  </Typography>

                  <Typography
                    sx={{
                      fontSize: 28,
                      fontWeight: 700,
                      color: "#111",
                      letterSpacing: "-0.03em",
                    }}
                  >
                    ${total.toFixed(2)}
                  </Typography>
                </Box>
                <Button
                  component="a"
                  href={getWhatsAppCheckoutLink()}
                  target="_blank"
                  rel="noopener noreferrer"
                  fullWidth
                  sx={{
                    mt: 3,
                    height: 50,
                    borderRadius: "16px",
                    textTransform: "none",
                    fontWeight: 700,
                    fontSize: 15,
                    bgcolor: "#25D366",
                    color: "#fff",
                    "&:hover": {
                      bgcolor: "#1ebe5d",
                    },
                  }}
                >
                  Comprar por WhatsApp
                </Button>

                <Button
                  onClick={() => navigate("/tienda")}
                  fullWidth
                  sx={{
                    mt: 1.2,
                    height: 46,
                    borderRadius: "16px",
                    textTransform: "none",
                    fontWeight: 700,
                    fontSize: 14,
                    border: "1px solid rgba(0,0,0,0.12)",
                    color: "#111",
                  }}
                >
                  Seguir comprando
                </Button>
              </Box>
            </Box>
          )}
        </Stack>
      </Container>
    </Box>
  );
}